// @oasis-permission-inherits: /videos (mirage-videos:view)
import { Link, createFileRoute } from "@tanstack/react-router";
import { Play, RefreshCw } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";
import { MirageEvalMetric, MirageEvalStatusBadge } from "~/modules/mirage-video-evals/components";
import { formatConfig, formatMirageEvalDate } from "~/modules/mirage-video-evals/formatters";
import {
  getMirageVideoEvalResultsQuery,
  getMirageVideoEvalTestQuery,
} from "~/modules/mirage-video-evals/queries";
import type {
  MirageVideoEvalAxisStats,
  MirageVideoEvalConfigRanking,
  MirageVideoEvalRanking,
} from "~/modules/mirage-video-evals/schemas";

export const Route = createFileRoute("/_dash/videos/evals/$testId/results")({
  component: RouteComponent,
});

function RouteComponent() {
  const { testId } = Route.useParams();
  const { data: test } = getMirageVideoEvalTestQuery.useQuery({
    pathParams: { testId },
  });
  const {
    data: results,
    isLoading,
    isFetching,
    error,
    refetch,
  } = getMirageVideoEvalResultsQuery.useQuery({
    pathParams: { testId },
  });

  if (isLoading) {
    return (
      <div className="rounded-md border p-5 text-sm text-muted-foreground">Loading results...</div>
    );
  }

  if (error || !results) {
    return (
      <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-medium">Failed to load results</p>
        {error && <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2">
            <Button asChild variant="link" size="sm" className="h-auto p-0">
              <Link to="/videos/evals/$testId" params={{ testId }}>
                Back to test
              </Link>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold">{test?.name ?? "Mirage Eval Results"}</h1>
            <MirageEvalStatusBadge status={results.status} />
          </div>
          <div className="mt-1 font-mono text-xs text-muted-foreground">{testId}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/videos/evals/$testId/rank" params={{ testId }}>
              <Play className="size-4" />
              Rank More
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <MirageEvalMetric label="Votes" value={results.voteCount} />
        <MirageEvalMetric label="Configs" value={results.configs.length} />
        <MirageEvalMetric label="Axes" value={results.axes.length} />
        <MirageEvalMetric label="Computed" value={formatMirageEvalDate(results.computedAt)} />
      </div>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Axis Coverage</h2>
        <AxisCoverageTable axes={results.axes} />
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Config Rankings</h2>
        <ConfigRankingTable configs={results.configs} />
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Video Rankings</h2>
        <VideoRankingTable videos={results.videos} />
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Per Clip</h2>
        <div className="space-y-4">
          {Object.entries(results.clips).map(([clipId, videos]) => (
            <div key={clipId} className="rounded-md border">
              <div className="border-b bg-muted/30 px-3 py-2 font-mono text-xs">{clipId}</div>
              <VideoRankingTable videos={videos} compact />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function AxisCoverageTable({ axes }: { axes: MirageVideoEvalAxisStats[] }) {
  if (axes.length === 0) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground">
        No axis coverage yet.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Axis</TableHead>
            <TableHead>Information</TableHead>
            <TableHead>Votes</TableHead>
            <TableHead>Winning Heat</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {axes.map((axis) => (
            <TableRow key={axis.axis}>
              <TableCell className="min-w-44 align-top">
                <div className="font-mono text-xs">{axis.axis}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {axis.observedValueCount}/{axis.possibleValueCount} values observed
                </div>
              </TableCell>
              <TableCell className="min-w-56 align-top">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <span>{formatPercent(axis.coverage)}</span>
                  <span className="font-mono text-muted-foreground">
                    {axis.observedPairCount}/{axis.possiblePairCount} pairs
                  </span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-sky-500"
                    style={{ width: `${Math.min(Math.max(axis.coverage, 0), 1) * 100}%` }}
                  />
                </div>
              </TableCell>
              <TableCell className="align-top font-mono text-xs">{axis.comparisonCount}</TableCell>
              <TableCell className="max-w-xl align-top">
                <AxisHeat axis={axis} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AxisHeat({ axis }: { axis: MirageVideoEvalAxisStats }) {
  const values = [...axis.values]
    .filter((value) => value.comparisons > 0)
    .sort(
      (left, right) =>
        Math.abs(right.score) - Math.abs(left.score) ||
        right.comparisons - left.comparisons ||
        right.score - left.score ||
        left.valueKey.localeCompare(right.valueKey)
    )
    .slice(0, 5);
  if (values.length === 0) {
    return <span className="text-xs text-muted-foreground">No comparisons yet.</span>;
  }

  const maxAbsScore = Math.max(1, ...values.map((value) => Math.abs(value.score)));
  return (
    <div className="space-y-2">
      {values.map((value) => {
        const heat = Math.min(Math.abs(value.score) / maxAbsScore, 1);
        return (
          <div key={value.valueKey} className="grid gap-1">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="max-w-80 truncate font-mono">{formatAxisValue(value.value)}</span>
              <span className="font-mono text-muted-foreground">
                {value.score.toFixed(2)} · {value.wins}W/{value.losses}L
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full rounded-full",
                  value.score >= 0 ? "bg-emerald-500" : "bg-destructive"
                )}
                style={{ width: `${Math.max(heat * 100, 4)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ConfigRankingTable({ configs }: { configs: MirageVideoEvalConfigRanking[] }) {
  if (configs.length === 0) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground">No config results.</div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Rank</TableHead>
            <TableHead>Score</TableHead>
            <TableHead>Record</TableHead>
            <TableHead>Coverage</TableHead>
            <TableHead>Config</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {configs.map((config, index) => (
            <TableRow key={config.configKey}>
              <TableCell>{index + 1}</TableCell>
              <TableCell className="font-mono text-xs">{config.score.toFixed(2)}</TableCell>
              <TableCell className="font-mono text-xs">
                {config.wins}W / {config.losses}L / {config.ties}T / {config.bothBad}BB
              </TableCell>
              <TableCell className="text-xs">
                {config.videoCount} videos, {config.clipCount} clips
              </TableCell>
              <TableCell className="max-w-2xl">
                <pre className="max-h-36 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-2 text-xs">
                  {formatConfig(config.config)}
                </pre>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function VideoRankingTable({
  videos,
  compact = false,
}: {
  videos: MirageVideoEvalRanking[];
  compact?: boolean;
}) {
  if (videos.length === 0) {
    return (
      <div className="rounded-md border p-4 text-sm text-muted-foreground">No video results.</div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Rank</TableHead>
          <TableHead>Video</TableHead>
          {!compact && <TableHead>Clip</TableHead>}
          <TableHead>Score</TableHead>
          <TableHead>Record</TableHead>
          {!compact && <TableHead>Config</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {videos.map((video, index) => (
          <TableRow key={video.videoId}>
            <TableCell>{index + 1}</TableCell>
            <TableCell className="font-mono text-xs">{video.videoId}</TableCell>
            {!compact && <TableCell className="font-mono text-xs">{video.clipId}</TableCell>}
            <TableCell className="font-mono text-xs">{video.score.toFixed(2)}</TableCell>
            <TableCell className="font-mono text-xs">
              {video.wins}W / {video.losses}L / {video.ties}T / {video.bothBad}BB
            </TableCell>
            {!compact && (
              <TableCell className="max-w-xl">
                <pre className="max-h-28 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-2 text-xs">
                  {formatConfig(video.config)}
                </pre>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function formatPercent(value: number) {
  return `${Math.round(Math.min(Math.max(value, 0), 1) * 100)}%`;
}

function formatAxisValue(value: unknown) {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value == null) {
    return String(value);
  }
  return JSON.stringify(value);
}
